import { MANUAL_MEAL_TYPES } from "../config/quoteBudgetManualEntryConfig";
import type { ManualEntryContext, ManualEntrySource } from "../types/manualEntry";

export const MEAL_ENTRY_ROLES = ["学生", "老师", "导游", "司机"] as const;
export type MealEntryField = "area" | "mode" | "role" | "meal" | "people" | "count" | "price";
export type MealEntryRow = {
  source: ManualEntrySource; area: string; role: string; meal: string; mode: "people" | "table";
  people: string; count: string; price: string; formula: string; linked: boolean; editable: boolean;
};
const roleKeys = { 学生: "studentCount", 老师: "teacherCount", 导游: "guideCount", 司机: "driverCount" } as const;
const roleCount = (role: string, context: ManualEntryContext) => role in roleKeys ? String(context.people[roleKeys[role as keyof typeof roleKeys]] ?? 0) : "";
const valueOf = (value = "") => /^(区域|角色|餐类|人数|餐数|桌数|数量|单价)$/.test(value) ? "" : value;
const matchingAreas = (value: string, context: ManualEntryContext) => (context.areas ?? []).filter(area => value ? area.name === value || area.id === value : area.id === context.areaId);

/** 保留解析器的跨片段继承；无效区域不能给后续记录带入角色和餐类。 */
export function mealEntryRows(text: string, context: ManualEntryContext): MealEntryRow[] {
  let area = context.areas?.find(item => item.id === context.areaId)?.name ?? "", role = "", meal = "";
  return [...text.matchAll(/[^\n;；,，]+/gu)].filter(match => match[0].trim()).map(match => {
    const start = (match.index ?? 0) + match[0].search(/\S/u), raw = match[0].trim();
    const source = { text: raw, start, end: start + raw.length, line: text.slice(0, start).split("\n").length };
    let rest = raw.normalize("NFKC");
    if (rest.startsWith("@")) {
      role = ""; meal = ""; area = "";
      const header = rest.match(/^@(.+?)(?=\s|学生|老师|导游|司机|正餐|早餐|中餐|晚餐|$)/u);
      if (header) { area = header[1]; rest = rest.slice(header[0].length).trim(); }
    }
    const matches = matchingAreas(area, context);
    const validArea = matches.length === 1 && (!context.areaId || matches[0].id === context.areaId);
    const roleMatch = rest.match(/^(学生|老师|导游|司机|司陪|角色)\s*/u);
    const rowRole = roleMatch ? valueOf(roleMatch[1]) : role;
    rest = rest.slice(roleMatch?.[0].length ?? 0).trim();
    const mealMatch = rest.match(/^(正餐|早餐|中餐|晚餐|餐类)\s*/u);
    const rowMeal = mealMatch ? valueOf(mealMatch[1]) : meal;
    rest = rest.slice(mealMatch?.[0].length ?? 0).trim();
    if (validArea) { role = rowRole; meal = rowMeal; }
    const mode = /桌/u.test(rest) ? "table" : "people";
    const factors = rest.replace(/\s/gu, "").split(/[xX×*]/u).map(value => value.replace(/(?<=\d)(?:人|餐|桌|元)$/u, ""));
    const linked = factors.length !== 3;
    return { source, area: valueOf(area), role: rowRole, meal: rowMeal, mode, formula: rest, linked,
      people: mode === "table" ? "" : linked ? roleCount(rowRole, context) : valueOf(factors[0]),
      count: valueOf(factors.at(-2)), price: valueOf(factors.at(-1)),
      editable: factors.length === 2 || (mode === "people" && factors.length === 3),
    };
  });
}

/** 将餐费继承上下文显式写回，避免改删前行影响后行。 */
function explicitText(row: MealEntryRow): string {
  const role = row.role || (row.mode === "people" ? "角色" : "");
  return `@${row.area || "区域"} ${role ? role + " " : ""}${row.meal || "餐类"}${row.formula}`;
}

/** 先固化各行原本继承的区域、角色与餐类，再改当前行；保留无法解析的原文。 */
function rewriteRows(text: string, rows: MealEntryRow[], index: number, replacement: string): string {
  let cursor = 0, result = "";
  rows.forEach((row, rowIndex) => {
    result += text.slice(cursor, row.source.start);
    result += rowIndex === index ? replacement : row.editable ? explicitText(row) : row.source.text;
    cursor = row.source.end;
  });
  result += text.slice(cursor);
  return result.replace(/^[\s;；,，]+|[\s;；,，]+$/gu, "");
}

/** 修改餐费字段时保留其他行的继承含义与人数联动。 */
export function changeMealEntryRow(text: string, rows: MealEntryRow[], index: number, field: MealEntryField, value: string, context: ManualEntryContext): string {
  const row = rows[index];
  if (!row?.editable || /[\n\r;；,，@＠×*]/u.test(value)) return text;
  if (["people", "count", "price"].includes(field) && /[xX]/u.test(value)) return text;
  if (field === "mode" && value !== "people" && value !== "table") return text;
  if (field === "meal" && value && !(MANUAL_MEAL_TYPES as readonly string[]).includes(value)) return text;
  if (field === "role" && value && !(MEAL_ENTRY_ROLES as readonly string[]).includes(value)) return text;
  if (field === "people" && (row.mode === "table" || row.role === "学生")) return text;
  if (field === "area" && value) {
    const matches = matchingAreas(value, context);
    if (matches.length !== 1 || (context.areaId && matches[0].id !== context.areaId)) return text;
  }
  const next = { ...row, [field]: value };
  if (field === "mode") { next.role = value === "people" ? "学生" : ""; next.linked = true; }
  if (field === "role") next.linked = true;
  if (field === "mode" || field === "role") next.people = roleCount(next.role, context);
  if (field === "people") next.linked = false;
  next.formula = next.mode === "table"
    ? `${next.count ? next.count + "桌" : "桌数"}×${next.price || "单价"}`
    : [next.linked ? null : next.people || "人数", next.count || "餐数", next.price || "单价"].filter(value => value !== null).join("×");
  return rewriteRows(text, rows, index, explicitText(next));
}

/** 删除当前餐费行并固化其他行原先继承的信息。 */
export function removeMealEntryRow(text: string, rows: MealEntryRow[], index: number): string {
  return rows[index] ? rewriteRows(text, rows, index, "") : text;
}

/** 按当前区域新增学生正餐待补价行。 */
export function appendMealEntryRow(text: string, context: ManualEntryContext, rows: MealEntryRow[]): string {
  const area = context.areas?.find(item => item.id === context.areaId)?.name || rows.filter(row => row.editable).at(-1)?.area || "区域";
  return `${text}${text && !text.endsWith("\n") ? "\n" : ""}@${area} 学生 正餐1×单价`;
}
